import { cronToSimpleDisplayText } from "@validereinc/utilities";
import classNames from "classnames/bind";
import cronValidate from "cron-validate";
import { isPlainObject } from "es-toolkit/compat";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useController,
  type FieldValues,
  type ValidateResult,
} from "react-hook-form";
import { ButtonToggleGroup } from "../../ButtonToggleGroup";
import { Checkbox } from "../../Checkbox";
import { IconLoading } from "../../Icon";
import {
  mergeProps,
  stripInvalidPropsForElement,
} from "../../utils/component-props";
import { InputWithLabels } from "../_components/InputWithLabels";
import { InvalidInputMessage } from "../_components/InvalidInputMessage";
import { DropdownInputBare } from "../DropdownInput";
import styles from "./CronInput.module.scss";
import {
  CRON_DAY_OPTIONS,
  CRON_INTERVALS,
  CRON_MONTH_OPTIONS,
  getDayOfMonthOptions,
  getHourOptions,
  getMinutesOptions,
} from "./helpers";
import {
  CronDayTypeType,
  CronInputProps,
  CronIntervalType,
  type ControlledCronInputProps,
  type CronIntervalTypeType,
} from "./types";

const cx = classNames.bind(styles);

const CronInput = forwardRef<HTMLFieldSetElement, CronInputProps>(
  (
    {
      value,
      onChange,
      error,
      invalidMessage,
      "aria-invalid": ariaInvalid,
      isDisabled,
      isLoading,
      isBusy,
      className,
      ...restProps
    },
    ref
  ) => {
    const initialized = useRef(false);
    const [selectedInterval, setSelectedInterval] =
      useState<CronIntervalTypeType>(CronIntervalType.WEEKLY);
    const onIntervalChange = (interval: string) => {
      setSelectedInterval(interval as CronIntervalTypeType);
    };
    const [validationError, setValidationError] = useState<string[] | null>(
      null
    );
    const [hourMinutes, setHourMinutes] = useState(["0", "0"]);
    const [weeklyConfig, setWeeklyConfig] = useState<{
      days: CronDayTypeType[];
    }>({
      days: [],
    });
    const [monthlyConfig, setMonthlyConfig] = useState<{
      months: number[];
      dayOfMonth: number;
    }>({
      months: [],
      dayOfMonth: 1,
    });
    const hoursOptions = useMemo(() => getHourOptions(), []);
    const minutesOptions = useMemo(() => getMinutesOptions(), []);
    const dayOfMonthOptions = useMemo(() => getDayOfMonthOptions(), []);

    const getCron = useCallback(() => {
      let cron = "";
      const daysOfWeek =
        weeklyConfig.days.length > 0 ? weeklyConfig.days.join(",") : "*";
      const months =
        monthlyConfig.months.length > 0 ? monthlyConfig.months.join(",") : "*";

      switch (selectedInterval) {
        case CronIntervalType.WEEKLY:
          cron = `${hourMinutes[1]} ${hourMinutes[0]} ? * ${daysOfWeek} *`;
          break;
        case CronIntervalType.MONTHLY:
          cron = `${hourMinutes[1]} ${hourMinutes[0]} ${monthlyConfig.dayOfMonth} ${months} ? *`;
          break;
        default:
          cron = "";
          break;
      }
      return cron;
    }, [
      selectedInterval,
      monthlyConfig,
      weeklyConfig,
      hourMinutes[0],
      hourMinutes[1],
    ]);
    const currentCron = useMemo(() => getCron(), [getCron]);
    const onChangeCron = (cron: string) => {
      const validationResult = cronValidate(cron, {
        preset: "aws-cloud-watch",
      });

      if (validationResult.isValid()) {
        setValidationError(null);
        onChange?.(cron);
      } else {
        setValidationError(validationResult.error);
      }
    };

    const parseCron = (cron: string) => {
      const validationResult = cronValidate(cron, {
        preset: "aws-cloud-watch",
      });

      if (validationResult.isValid()) {
        setValidationError(null);
        const value = validationResult.value;
        setHourMinutes([value.hours, value.minutes]);
        if (value.daysOfMonth !== "?" && value.daysOfMonth !== "*") {
          setSelectedInterval(CronIntervalType.MONTHLY);
          setMonthlyConfig((prev) => ({
            ...prev,
            months:
              value.months !== "?" && value.months !== "*"
                ? value.months.split(",").map((month) => parseInt(month))
                : [],
            dayOfMonth:
              value.daysOfMonth !== "?" && value.daysOfMonth !== "*"
                ? (value.daysOfMonth
                    .split(",")
                    .map((day) => parseInt(day))[0] ?? 1)
                : 1,
          }));
        } else {
          setWeeklyConfig((prev) => ({
            ...prev,
            days:
              value.daysOfWeek !== "?" && value.daysOfWeek !== "*"
                ? (value.daysOfWeek.split(",").sort() as CronDayTypeType[])
                : [],
          }));
          setSelectedInterval(CronIntervalType.WEEKLY);
        }
      } else {
        setValidationError(validationResult.error);
      }
    };

    //update value when options change
    useEffect(() => {
      const cron = getCron();
      if (!isDisabled) {
        onChangeCron?.(cron);
      }
    }, [getCron]);
    //parse initial cron to state on mount
    useEffect(() => {
      if (!initialized.current && !!value) {
        initialized.current = true;
        parseCron(value);
      }
    }, [value]);

    return (
      <fieldset
        {...stripInvalidPropsForElement(restProps, "fieldset")}
        name={restProps.name}
        className={cx("root", className)}
        aria-invalid={ariaInvalid}
        aria-busy={isBusy}
        ref={ref}
      >
        <legend className={cx("legend-hidden")}>
          Schedule Configuration Input
        </legend>
        <ButtonToggleGroup
          name={`${restProps.name}-interval`}
          activeKey={selectedInterval}
          items={CRON_INTERVALS}
          onChange={onIntervalChange}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isBusy={isBusy}
          isFluid
        />
        {selectedInterval === CronIntervalType.WEEKLY && (
          <>
            <div className={cx("checkbox-container")}>
              {CRON_DAY_OPTIONS.map(({ label, value }) => {
                return (
                  <Checkbox
                    key={value}
                    name={`days-of-week-${value}`}
                    label={label}
                    isInline
                    isDisabled={isDisabled}
                    error={error}
                    value={weeklyConfig.days.includes(value as CronDayTypeType)}
                    onChange={(include) => {
                      if (include) {
                        setWeeklyConfig((prev) => ({
                          ...prev,
                          days: [...prev.days, value as CronDayTypeType].sort(),
                        }));
                      } else {
                        setWeeklyConfig((prev) => ({
                          ...prev,
                          days: prev.days.filter((day) => day !== value).sort(),
                        }));
                      }
                    }}
                    isLoading={isLoading}
                    isBusy={isBusy}
                  />
                );
              })}
            </div>
          </>
        )}
        {selectedInterval === CronIntervalType.MONTHLY && (
          <>
            <div className={cx("checkbox-container")}>
              {CRON_MONTH_OPTIONS.map(({ label, value }) => {
                return (
                  <Checkbox
                    key={value}
                    name={`${restProps.name}-days-of-month-${value}`}
                    label={label}
                    isInline
                    isDisabled={isDisabled}
                    error={error}
                    value={monthlyConfig.months.includes(value)}
                    onChange={(include) => {
                      if (include) {
                        setMonthlyConfig((prev) => ({
                          ...prev,
                          months: [...prev.months, value].sort(),
                        }));
                      } else {
                        setMonthlyConfig((prev) => ({
                          ...prev,
                          months: prev.months
                            .filter((month) => month !== value)
                            .sort(),
                        }));
                      }
                    }}
                    isLoading={isLoading}
                    isBusy={isBusy}
                  />
                );
              })}
            </div>
            <div className={cx("input-container")}>
              <label
                htmlFor={`${restProps.name}-day-of-month`}
                className={cx("label")}
              >
                Day of Month
              </label>
              <DropdownInputBare
                name={`${restProps.name}-day-of-month`}
                labelKey="label"
                valueKey="value"
                isFluid
                error={error}
                invalidMessage={invalidMessage}
                aria-invalid={ariaInvalid}
                isDisabled={isDisabled}
                options={dayOfMonthOptions}
                onChange={(value: number) => {
                  setMonthlyConfig((prev) => ({
                    ...prev,
                    dayOfMonth: value,
                  }));
                }}
                isClearable={false}
                value={monthlyConfig.dayOfMonth.toString()}
                isLoading={isLoading}
                isBusy={isBusy}
              />
            </div>
          </>
        )}
        <div className={cx("hour-minutes-container")}>
          <div className={cx("input-container")}>
            <label
              htmlFor={`${restProps.name}-hour`}
              className={cx("label")}
            >
              Hour
            </label>
            <DropdownInputBare
              name={`${restProps.name}-hour`}
              labelKey="label"
              valueKey="value"
              error={error}
              invalidMessage={invalidMessage}
              aria-invalid={ariaInvalid}
              isDisabled={isDisabled}
              options={hoursOptions}
              onChange={(value: number) => {
                setHourMinutes([value.toString(), hourMinutes[1]]);
              }}
              value={hourMinutes[0]}
              isLoading={isLoading}
              isBusy={isBusy}
              isClearable={false}
              className={cx("hour-minutes-input")}
            />
          </div>
          <div className={cx("input-container")}>
            <label
              htmlFor={`${restProps.name}-minute`}
              className={cx("label")}
            >
              Minutes
            </label>
            <DropdownInputBare
              name={`${restProps.name}-minute`}
              labelKey="label"
              valueKey="value"
              error={error}
              invalidMessage={invalidMessage}
              aria-invalid={ariaInvalid}
              isDisabled={isDisabled}
              options={minutesOptions}
              onChange={(value: number) => {
                setHourMinutes([hourMinutes[0], value.toString()]);
              }}
              value={hourMinutes[1]}
              isLoading={isLoading}
              isBusy={isBusy}
              isClearable={false}
              className={cx("hour-minutes-input")}
            />
          </div>
        </div>
        <div>
          {currentCron && <div>{cronToSimpleDisplayText(currentCron)}</div>}
        </div>
        {validationError ? (
          <InvalidInputMessage
            intent="error"
            isDisabled={isDisabled ?? false}
            isBusy={isBusy ?? false}
            isLoading={isLoading ?? false}
          >
            {validationError.join(", ")}
          </InvalidInputMessage>
        ) : null}
        {isBusy && (
          <IconLoading
            speed="fast"
            size={16}
          />
        )}
      </fieldset>
    );
  }
);

CronInput.displayName = "CronInput";

export const ControlledCronInput = <T extends FieldValues>({
  name,
  defaultValue,
  rules,
  label,
  isLabelShown,
  labelPosition,
  description,
  isRequired,
  isOptionalTextShown,
  shouldShowDescriptionInTooltip,
  isInline = false,
  isFluid = true,
  ...restProps
}: ControlledCronInputProps<T>) => {
  const { isDisabled, isLoading, isBusy } = restProps;
  const { field, fieldState } = useController({
    name,
    defaultValue,
    rules: {
      ...rules,
      validate:
        rules?.validate && typeof rules.validate === "function"
          ? rules.validate
          : {
              ...(isRequired
                ? {
                    isRequired: (value: string): ValidateResult =>
                      [null, undefined, ""].includes(value)
                        ? `${typeof label === "string" ? label : "Cron input"} is required`
                        : undefined,
                  }
                : {}),
              ...(rules?.validate && isPlainObject(rules.validate)
                ? rules.validate
                : {}),
            },
    },
  });

  const mergedProps = useMemo(() => {
    return mergeProps(restProps, {
      onChange: field.onChange,
      onBlur: field.onBlur,
    });
  }, [restProps, field.onChange, field.onBlur]);

  const invalidMessage = fieldState.invalid
    ? (fieldState.error?.message ??
      `Input is invalid (${fieldState.error?.type})`)
    : undefined;

  return (
    <InputWithLabels
      colorScheme="light"
      isDisabled={isDisabled ?? false}
      isLoading={isLoading ?? false}
      isBusy={isBusy ?? false}
      label={label}
      isLabelShown={isLabelShown}
      labelPosition={labelPosition}
      description={description}
      isRequired={isRequired}
      isOptionalTextShown={isOptionalTextShown}
      shouldShowDescriptionInTooltip={false}
      isInline={isInline}
      isFluid={isFluid}
      invalidMessage={invalidMessage}
    >
      {({ invalidMessageId, inputId, labelId }) => (
        <CronInput
          id={inputId}
          labelledById={labelId}
          error={fieldState.error}
          aria-invalid={fieldState.invalid}
          invalidMessage={invalidMessage}
          aria-describedby={invalidMessageId}
          {...(!isLabelShown && !description && typeof label === "string"
            ? { "aria-label": label }
            : {})}
          {...field}
          {...mergedProps}
        />
      )}
    </InputWithLabels>
  );
};

ControlledCronInput.displayName = "ControlledCronInput";

export default CronInput;
